import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tenantApi } from '../../api/endpoints/tenant';
import { useAuth } from '../../hooks/useAuth';
import { SELECT_CLASS } from '../../lib/selectClass';
import { allowedScopesForRole, canActorAssignScope, groupRolesByScope, strongestActorScope } from '../../lib/roleScopes';
import { LevelEntityPicker, isGenericLevel } from '../scoped/LevelEntityPicker';
import { Label } from '../ui/label';
import type { Scope } from '../../api/types/permissions';

/**
 * Selección rol → scope → scopeRef, EXTRAÍDA de `RoleAssignmentDialog` para reusarla en
 * `ManageUserRolesDialog`. Es un campo controlado: maneja su propio estado interno (rol elegido,
 * retailer encadenado y refs marcados) y notifica al padre vía `onChange` el resultado normalizado
 * a asignar: `{ roleId, roleScope, refsToAssign }` (o `null` mientras falte rol/destino).
 *
 *  - TENANT      → refsToAssign = [tenantId]
 *  - PLATFORM    → refsToAssign = ['*']
 *  - LEVEL_1/2/3 → picker de entidades de nivel (`<LevelEntityPicker>`): L1 multi-select directo;
 *                  L2/L3 eligen la cadena de padres y luego multi-select de hojas (refsToAssign = ids).
 *
 * Los roles del dropdown van AGRUPADOS por nivel (`groupRolesByScope`) y filtrados a los que el
 * actor puede asignar (`canActorAssignScope`, espeja el rank del backend). El backend re-enforza.
 */

export interface RoleScopeSelection {
    roleId: string;
    roleScope: Scope;
    refsToAssign: string[];
}

interface Props {
    /** Tenant donde se asigna (drill-down platform o el del actor en retailer). */
    tenantId: string;
    /** Solo dispara queries cuando el contenedor (dialog) está abierto. */
    open: boolean;
    /** Notifica la selección normalizada lista para asignar, o `null` si está incompleta. */
    onChange: (selection: RoleScopeSelection | null) => void;
    /** Prefijo para los `id`/`htmlFor` (evita colisiones si hay 2 instancias montadas). */
    idPrefix?: string;
}

export function RoleScopeFields({ tenantId, open, onChange, idPrefix = 'rsf' }: Props) {
    const { roleAssignments, hasPermission } = useAuth();
    // Scope del actor + create.lateral → solo se ofrecen roles que el actor puede asignar (espeja backend).
    const actorScope = strongestActorScope(roleAssignments);
    const canLateral = hasPermission('tenant.user.create.lateral');

    const [roleId, setRoleId] = useState<string | null>(null);
    // scopeRefs marcados en el multi-select (LEVEL_1 → entityIds).
    const [selectedRefs, setSelectedRefs] = useState<string[]>([]);

    const { data: roles } = useQuery({
        queryKey: ['tenant', tenantId, 'roles'],
        queryFn: () => tenantApi.listRoles(tenantId),
        enabled: open,
    });

    // Solo roles cuyo scope el actor puede asignar (usa el scope REAL del rol → vale para custom).
    const assignableRoles = useMemo(
        () =>
            (roles?.items ?? []).filter((r) =>
                canActorAssignScope(actorScope, r.scope, canLateral),
            ),
        [roles, actorScope, canLateral],
    );
    const groupedRoles = useMemo(() => groupRolesByScope(assignableRoles), [assignableRoles]);

    // Nivel del destino = scope REAL del rol elegido. Fallback a la matriz seed solo si el list no
    // lo trae (backend viejo / rol custom sin scope).
    const roleScope = useMemo<Scope | null>(() => {
        if (!roleId) return null;
        const item = roles?.items.find((r) => r.roleId === roleId);
        return item?.scope ?? allowedScopesForRole(roleId)[0];
    }, [roleId, roles]);

    // Notifica al padre la selección normalizada (o null si incompleta). Se llama en cada handler de
    // cambio en vez de en un effect (el lint del repo prohíbe setState/efectos derivados acá).
    // scopeRefs efectivos: TENANT → [tenantId]; PLATFORM → ['*']; LEVEL_1 → los marcados.
    const notify = (nextRoleId: string | null, nextRefs: string[]): void => {
        if (!nextRoleId) {
            onChange(null);
            return;
        }
        const item = roles?.items.find((r) => r.roleId === nextRoleId);
        // `allowedScopesForRole` siempre devuelve al menos un scope (fallback CUSTOM) → no es undefined.
        const scope: Scope = item?.scope ?? allowedScopesForRole(nextRoleId)[0];
        const fixed = scope === 'TENANT' ? tenantId : scope === 'PLATFORM' ? '*' : null;
        const refs = fixed !== null ? [fixed] : nextRefs;
        onChange(
            refs.length > 0 ? { roleId: nextRoleId, roleScope: scope, refsToAssign: refs } : null,
        );
    };

    const handleRoleChange = (id: string): void => {
        setRoleId(id);
        setSelectedRefs([]);
        notify(id, []);
    };
    // El picker de nivel entrega el array completo de hojas marcadas; se refleja y se notifica.
    const handleLevelChange = (refs: string[]): void => {
        setSelectedRefs(refs);
        notify(roleId, refs);
    };

    return (
        <div className="space-y-4">
            <div className="space-y-1">
                <select
                    id={`${idPrefix}-role`}
                    aria-label="Rol"
                    className={SELECT_CLASS}
                    value={roleId ?? ''}
                    onChange={(e) => {
                        handleRoleChange(e.target.value);
                    }}
                >
                    <option value="" disabled>
                        Elegir role
                    </option>
                    {groupedRoles.map((g) => (
                        <optgroup key={g.key} label={g.label}>
                            {g.roles.map((r) => (
                                <option key={r.roleId} value={r.roleId}>
                                    {r.roleName}
                                </option>
                            ))}
                        </optgroup>
                    ))}
                </select>
            </div>

            {roleScope === 'TENANT' && (
                <div className="space-y-1">
                    <Label htmlFor={`${idPrefix}-dest-tenant`}>Destino (tenant)</Label>
                    <input
                        id={`${idPrefix}-dest-tenant`}
                        className={SELECT_CLASS}
                        value={tenantId}
                        readOnly
                        disabled
                    />
                </div>
            )}

            {roleScope === 'PLATFORM' && (
                <div className="space-y-1">
                    <Label htmlFor={`${idPrefix}-dest-platform`}>Destino (plataforma)</Label>
                    <input
                        id={`${idPrefix}-dest-platform`}
                        className={SELECT_CLASS}
                        value="*"
                        readOnly
                        disabled
                    />
                </div>
            )}

            {isGenericLevel(roleScope) && (
                // DEC-RBAC-090 / DEC-027: destino de un rol de nivel genérico (L1/L2/L3). `key={roleScope}`
                // remonta el picker al cambiar de nivel → reinicia su cadena de padres interna.
                <LevelEntityPicker
                    key={roleScope}
                    tenantId={tenantId}
                    level={roleScope}
                    value={selectedRefs}
                    onChange={handleLevelChange}
                    idPrefix={idPrefix}
                    open={open}
                />
            )}
        </div>
    );
}
